import { useEffect, useRef, useState } from "react";
import { Socket } from "socket.io-client";
import IOrder from "../../../models/IOrder";
import IPayment from "../../../models/IPayment";
import ISupplierModel from "../../../models/ISupplierModel";
import IUserUI from "../../../models/IUserUI";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import IQBOResendPaymentDTO from "../../../models/DTO/IQBOResendPaymentDTO";
import IQBOOnResendPaymentDTO from "../../../models/DTO/IQBOOnResendPaymentDTO";
import INotification from "../../../models/INotification";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../models/EndpointsEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import PaymentTypesEnum from "../../../models/PaymentTypesEnum";
import QBOPaymentSyncStatusEnum from "../../../models/QBOPaymentSyncStatusEnum";
import UserTypesEnum from "../../../models/UserTypesEnum";
import HelperService from "../../../services/HelperService";
import UserService from "../../../services/UserService";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwPill from "../../shared/OwPill/OwPill";

type Props = {
    order: IOrder;
    socket: Socket;
    supplierModel: ISupplierModel;
    userModel: IUserUI;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    /** Reloads the order (and so each payment's QuickBooks status) after a resend. */
    getOrder: () => void;
};

/** Pill per QuickBooks status (API: QuickbooksOnlineService.getPaymentSyncStatus); no status = no pill. */
const QBO_STATUS_PILLS: { [status in QBOPaymentSyncStatusEnum]: { title: string; className: string; leftIconClass?: string } } = {
    [QBOPaymentSyncStatusEnum.SYNCED]: { title: "synced_to_quickbooks", className: "bg-secondary", leftIconClass: "fas fa-check" },
    [QBOPaymentSyncStatusEnum.DEPOSIT]: { title: "qbo_payment_status_deposit", className: "bg-secondary", leftIconClass: "fas fa-check" },
    [QBOPaymentSyncStatusEnum.UNAPPLIED]: { title: "qbo_payment_status_unapplied", className: "bg-info", leftIconClass: "fas fa-check" },
    [QBOPaymentSyncStatusEnum.WAITING_FOR_INVOICE]: {
        title: "qbo_payment_status_waiting_for_invoice",
        className: "bg-light text-dark border",
        leftIconClass: "far fa-clock",
    },
    [QBOPaymentSyncStatusEnum.SENDING]: { title: "qbo_payment_status_sending", className: "bg-info", leftIconClass: "fas fa-sync" },
    [QBOPaymentSyncStatusEnum.NOT_SENT]: {
        title: "qbo_payment_status_not_sent",
        className: "bg-danger",
        leftIconClass: "fas fa-exclamation-triangle",
    },
    [QBOPaymentSyncStatusEnum.BLOCKED]: {
        title: "qbo_payment_status_blocked",
        className: "bg-warning text-dark",
        leftIconClass: "fas fa-exclamation-triangle",
    },
};

/** Toast after a resend, by the status the payment ended up in. */
const RESEND_RESULT_NOTIFICATIONS: { [status in QBOPaymentSyncStatusEnum]: { message: string; level: NotificationLevelEnum } } = {
    [QBOPaymentSyncStatusEnum.SYNCED]: { message: "payment_sent_to_quickbooks", level: NotificationLevelEnum.success },
    [QBOPaymentSyncStatusEnum.DEPOSIT]: { message: "payment_sent_to_quickbooks", level: NotificationLevelEnum.success },
    [QBOPaymentSyncStatusEnum.UNAPPLIED]: { message: "payment_sent_to_quickbooks", level: NotificationLevelEnum.success },
    [QBOPaymentSyncStatusEnum.WAITING_FOR_INVOICE]: {
        message: "qbo_payment_resend_waiting_for_invoice",
        level: NotificationLevelEnum.info,
    },
    [QBOPaymentSyncStatusEnum.SENDING]: { message: "qbo_payment_resend_in_progress", level: NotificationLevelEnum.info },
    [QBOPaymentSyncStatusEnum.NOT_SENT]: { message: "qbo_payment_resend_not_sent", level: NotificationLevelEnum.warning },
    [QBOPaymentSyncStatusEnum.BLOCKED]: { message: "qbo_payment_resend_blocked", level: NotificationLevelEnum.warning },
};

export const OrderEntryPaymentsPanel = (props: Props) => {
    // Guid of the payment being resent (one at a time). A ref too, so the socket handlers registered once on
    // mount can tell our own response apart from another panel's.
    const [resendingGuid, setResendingGuid] = useState<string | null>(null);
    const resendingGuidRef = useRef<string | null>(null);

    // Like creating a QBO invoice, resending a payment is for business admins.
    const canResend = props.userModel.isSupplierUser && props.userModel.supplierUserToken?.User_Type?.id === UserTypesEnum.ADMIN;

    const setResending = (paymentGuid: string | null) => {
        resendingGuidRef.current = paymentGuid;
        setResendingGuid(paymentGuid);
    };

    const onResendToQuickbooks = (data: IQBOOnResendPaymentDTO) => {
        if (!resendingGuidRef.current || data.paymentGuid !== resendingGuidRef.current) {
            return;
        }

        setResending(null);

        const result = data.qbo_sync_status ? RESEND_RESULT_NOTIFICATIONS[data.qbo_sync_status] : undefined;

        if (result) {
            const notification: INotification = { ...result, position: NotificationPositionEnum.bc };
            props.notifyFn(null, notification);
        }

        props.getOrder();
    };

    // The error itself is toasted app-wide (EndpointsEnum.Notify); here we only clear the busy state.
    const onNotify = (data: IErrorDTO) => {
        if (resendingGuidRef.current && data?.title === "ERROR_RESEND_QBO_PAYMENT") {
            setResending(null);
            props.getOrder();
        }
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.PaymentsOnResendToQuickbooks, fn: onResendToQuickbooks },
        { endpoint: EndpointsEnum.Notify, fn: onNotify },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            props.socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                props.socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const resendToQuickbooks = (payment: IPayment) => {
        if (resendingGuidRef.current) {
            return;
        }

        setResending(payment.guid);

        const payload: IQBOResendPaymentDTO = {
            userGuid: props.userModel.guid,
            userToken: props.userModel.token,
            supplierGuid: props.supplierModel.guid,
            paymentGuid: payment.guid,
        };

        props.socket.emit(EndpointsEnum.PaymentsResendToQuickbooks, payload);
    };

    const paymentTypeKey = (paymentTypeId: number | null | undefined): string => {
        switch (paymentTypeId) {
            case PaymentTypesEnum.CREDIT_CARD:
                return "credit_card";
            case PaymentTypesEnum.CASH:
                return "cash";
            case PaymentTypesEnum.CHECK:
                return "check";
            case PaymentTypesEnum.TERMS:
                return "net_terms";
            case PaymentTypesEnum.SALES_REP:
                return "sales_rep";
            default:
                return "payment";
        }
    };

    const getStatusPill = (payment: IPayment) => {
        if (payment.is_voided) {
            return <OwPill title={"voided"} className={"ps-2 pe-2 bg-danger"} />;
        }

        if (payment.is_refund) {
            return <OwPill title={"refunded"} className={"ps-2 pe-2 bg-info"} />;
        }

        if (payment.is_captured) {
            return <OwPill title={"captured"} className={"ps-2 pe-2 bg-success"} />;
        }

        return <OwPill title={"authorized"} className={"ps-2 pe-2"} />;
    };

    const getQuickbooksStatus = (payment: IPayment) => {
        const pill = payment.qbo_sync_status ? QBO_STATUS_PILLS[payment.qbo_sync_status] : undefined;

        if (!pill) {
            return null;
        }

        return (
            <>
                <OwPill title={pill.title} className={`ps-2 pe-2 ms-2 ${pill.className}`} leftIconClass={pill.leftIconClass} />
                {canResend && payment.qbo_sync_status === QBOPaymentSyncStatusEnum.NOT_SENT ? (
                    <OwButton
                        className={"btn-outline-secondary btn-sm ms-2"}
                        title={"resend_to_quickbooks"}
                        leftIconClass={"fas fa-redo"}
                        active={resendingGuid === payment.guid}
                        disableOnActive={true}
                        disabled={!!resendingGuid && resendingGuid !== payment.guid}
                        fn={() => resendToQuickbooks(payment)}
                    />
                ) : null}
            </>
        );
    };

    const getPaymentRow = (payment: IPayment) => {
        const amount = HelperService.formatCurrency({ value: payment.capture_amount ?? payment.auth_amount });

        const receivedAt = UserService.parseLastUpdated(payment.received_at || payment.created_at, true);

        return (
            <div key={payment.guid} className={"border border-dark-subtle border-1 rounded p-2 mb-2"}>
                <div className={"d-flex align-items-center flex-wrap"}>
                    <b>
                        <OwLabel title={paymentTypeKey(payment.payment_type_id)} className={"me-2"} />
                    </b>
                    <b className={"me-2"}>{amount}</b>
                    {getStatusPill(payment)}
                    {getQuickbooksStatus(payment)}
                    {payment.reference ? (
                        <div className={"ps-2 pe-2 ms-2 border border-dark-subtle border-1 rounded-5 text-small"}>
                            <b>{payment.reference}</b>
                        </div>
                    ) : null}
                </div>
                <div className={"text-small text-muted"}>{receivedAt}</div>
            </div>
        );
    };

    return (
        <div className={"OrderEntryPaymentsPanel"}>
            <div className="panel panel-default p-2">
                <div className="panel-body">
                    {props.order.Payments?.map((payment) => {
                        return getPaymentRow(payment);
                    })}
                    {!props.order.Payments?.length && (
                        <div className="">
                            <OwLabel title={"no_payments_found"} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
